import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import AreaDataTable from './AreaDataTable';
import {
  getAreas,
  createArea,
  updateArea,
  deleteArea,
  checkAreaDuplicate,
} from '../api/areaAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { useSetting } from '../utils/AppSettingsContext';
import { validateForm as validateFormUtil } from '../utils/useValidator';

const AreaLayer = () => {
  const userProfile = useUserProfile();
  const showExtraAssetFields = useSetting('show_extra_asset_fields');
  const useFunctionalLocation = useSetting('use_functional_location');
  const defaultCostCentre = useSetting('default_cost_centre');
  const defaultCostCentreId =
    defaultCostCentre !== null && defaultCostCentre !== undefined
      ? Number(defaultCostCentre) || null
      : null;
  const [areaList, setAreaList] = useState([]);
  const [newArea, setNewArea] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);

  const tableFilters = useMemo(
    () => [
      { field: 'departmentId', type: 'dropDown', label: 'Department', api: 'hierarchy-structures', displayField: 'name', menuBarFilter: 1 },
      { field: 'teamId', type: 'dropDown', label: 'Team', api: 'teams', displayField: 'teamName', menuBarFilter: 1 },
      { field: 'costCenterId', type: 'dropDown', label: 'Cost Center', api: 'cost-centres', displayField: 'costCenterDescription' },
    ],
    [],
  );

  const {
    appliedFilterParams,
    renderFilterControls,
  } = useTableFilters(tableFilters);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);

  const filteredAreas = useMemo(() => {
    let list = areaList;
    if (appliedFilterParams.departmentId?.length) {
      list = list.filter(a => appliedFilterParams.departmentId.includes(a.departmentId));
    }
    if (appliedFilterParams.teamId?.length) {
      list = list.filter(a => appliedFilterParams.teamId.includes(a.teamId));
    }
    if (appliedFilterParams.costCenterId?.length) {
      list = list.filter(a => appliedFilterParams.costCenterId.includes(a.costCenterId));
    }
    return list;
  }, [areaList, appliedFilterParams]);

  const fetchAreas = async () => {
    try {
      const data = await getAreas();
      setAreaList(data);
    } catch (err) {
      console.error('Error fetching areas:', err);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const displayedAreas = useMemo(() => {
    if (!searchTerm) return filteredAreas;
    const term = searchTerm.toLowerCase();
    return filteredAreas.filter(
      (a) =>
        a.areaCode?.toLowerCase().includes(term) ||
        a.name?.toLowerCase().includes(term) ||
        a.description?.toLowerCase().includes(term) ||
        a.department?.name?.toLowerCase().includes(term) ||
        a.team?.teamName?.toLowerCase().includes(term) ||
        a.costCenter?.costCenterDescription?.toLowerCase().includes(term),
    );
  }, [filteredAreas, searchTerm]);

  useEffect(() => {
    const div = document.getElementById('imageBlock1');
    if (!div) return;

    if (showModal && newArea.image) {
      div.style.backgroundImage = `url(${process.env.REACT_APP_SPACES_BASE}/${newArea.image})`;
      div.style.backgroundSize = 'cover';
      div.style.backgroundPosition = 'center';
    } else {
      div.style.backgroundImage = '';
    }
  }, [showModal, editingId, newArea.image]);

  useEffect(() => {
    if (
      showModal &&
      !editingId &&
      defaultCostCentreId &&
      !newArea.costCenterId
    ) {
      setNewArea((prev) => ({ ...prev, costCenterId: defaultCostCentreId }));
    }
  }, [showModal, editingId, defaultCostCentreId, newArea.costCenterId]);

  const handleSave = async () => {
    const validationErrors = validateFormUtil(inputFields, newArea);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      const dup = await checkAreaDuplicate(
        newArea.areaCode?.trim(),
        newArea.name?.trim(),
        editingId,
      );
      const dupErrors = {};
      if (dup.codeExists) dupErrors.areaCode = 'Code already exists';
      if (dup.nameExists) dupErrors.name = 'Name already exists';
      if (Object.keys(dupErrors).length) {
        setErrors((prev) => ({ ...prev, ...dupErrors }));
        return;
      }

      const payload = {};
      for (const key in newArea) {
        if (newArea[key] !== null && typeof newArea[key] !== 'object') {
          payload[key] = newArea[key];
        }
      }

      if (editingId) {
        payload.updatedAt = new Date().toISOString();
        payload.updatedBy = userProfile?.id;
        await updateArea(editingId, payload);
      } else {
        payload.createdBy = userProfile?.id;
        await createArea(payload);
      }
      setShowModal(false);
      setEditingId(null);
      setNewArea({});
      await fetchAreas();
    } catch (err) {
      console.error('Error saving area:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this area?')) return;
    try {
      await deleteArea(id);
      await fetchAreas();
    } catch (err) {
      console.error('Error deleting area:', err);
    }
  };

  const generateCsv = () => {
    const headers = [
      'Code',
      'Name',
      'Description',
      'Department',
      'Team',
      'Cost Center',
    ];
    const rows = displayedAreas.map((row) => [
      row.areaCode || '',
      row.name || '',
      row.description || '',
      row.department?.name || '',
      row.team?.teamName || '',
      row.costCenter?.costCenterDescription || '',
    ]);
    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = [
      'Code',
      'Name',
      'Description',
      'Department',
      'Team',
      'Cost Center',
    ];
    const rows = displayedAreas.map((row) => [
      row.areaCode || '',
      row.name || '',
      row.description || '',
      row.department?.name || '',
      row.team?.teamName || '',
      row.costCenter?.costCenterDescription || '',
    ]);
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Areas');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'areas.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'areas.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const inputFields = useMemo(
    () => [
      { field: 'departmentId', label: 'Department', type: 'select', size: 6, api: 'hierarchy-structures', displayField: 'name', required: true },
      { field: 'imageBlock1', label: 'Image', type: 'emptyBorder', size: 6 },
      { field: 'areaCode', label: 'Area Code', type: 'text', size: 6, required: true },
      { field: 'generalSpacer1', label: '', type: 'empty', size: 6 },
      { field: 'name', label: 'Name', type: 'text', size: 6, required: true },
      { field: 'generalSpacer2', label: '', type: 'empty', size: 6 },
      { field: 'costCenterId', label: 'Cost Center', type: 'select', size: 6, api: 'cost-centres', displayField: 'costCenterDescription', required: true },
      { field: 'generalSpacer3', label: '', type: 'empty', size: 6 },
      { field: 'description', label: 'Description', type: 'text', size: 6 },
      { field: 'generalSpacer4', label: '', type: 'empty', size: 6 },
      { field: 'parentAreaId', label: 'Parent Area', type: 'select', size: 6, api: 'areas', displayField: 'name' },
      { field: 'teamId', label: 'Primary Team', type: 'select', size: 6, api: 'teams', displayField: 'teamName' },
      { field: 'floc', label: 'FLOC', type: 'text', size: 6 },
      { field: 'planningGroupId', label: 'Planning Group', type: 'select', size: 6, api: 'planning-group', displayField: 'planningGroupName' },
      { field: 'image', label: 'Area Image', type: 'image', size: 12, directory: 'modelimages' },
    ],
    [],
  );

  const { renderField } = useFormBuilder(inputFields, newArea, setNewArea, errors);

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type='text'
            className='form-control form-control-sm'
            style={{ width: '200px' }}
            placeholder='Search...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className='card h-100 p-0 rounded-0'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <div className='d-flex flex-wrap align-items-center gap-2 ms-3'>
            {renderFilterControls()}
          </div>
          <div className='d-flex align-items-center gap-2 ms-auto me-8'>
            <div className='dropdown me-2'>
              <button
                className='btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle'
                type='button'
                data-bs-toggle='dropdown'
                aria-expanded='false'
              >
                Export Data
              </button>
              <ul className='dropdown-menu p-12 border bg-base shadow'>
                <li>
                  <button className='dropdown-item' onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className='dropdown-item' onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>
            {userProfile?.privileges?.includes('areas:create') && (
              <button
                className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
                onClick={() => { setNewArea({}); setEditingId(null); setShowModal(true); }}
              >
                <Icon icon='ic:baseline-plus' className='icon text-xl' />
                Add Area
              </button>
            )}
          </div>
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive scroll-sm' style={{ maxHeight: "calc(100vh - 199px)" }}>
          <AreaDataTable
            areas={displayedAreas}
            onEdit={(row) => { setNewArea(row); setEditingId(row.id); setShowModal(true); }}
            onDelete={handleDelete}
            canEdit={userProfile?.privileges?.includes('areas:update')}
            canDelete={userProfile?.privileges?.includes('areas:delete')}
          />
        </div>
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                {newArea.image && (
                  <img
                    src={`${process.env.REACT_APP_SPACES_BASE}/${newArea.image}`}
                    alt='area'
                    className='rounded-circle me-3'
                    style={{ height: '50px', width: '50px', objectFit: 'cover' }}
                  />
                )}
                <h5 className='modal-title'>{editingId ? 'Edit Area' : 'Add Area'}</h5>
                <button type='button' className='btn-close' onClick={() => setShowModal(false)} />
              </div>
              <div className='modal-body'>
                <div className='row g-3'>
                  {inputFields
                    .filter((f) => useFunctionalLocation || f.field !== 'floc')
                    .filter((f) => showExtraAssetFields || f.field !== 'planningGroupId')
                    .map(renderField)}
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-secondary' onClick={() => setShowModal(false)}>Close</button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </>
  );
};

export default AreaLayer;
